import React, { useCallback, useEffect, useState } from "react";
import Axios from "axios";
import { message, Tooltip } from "antd";
import ContentBlock from "../Layout/ContentBlock";
import ContentValidation from "../common/ContentValidation";
import TableUi from "../Layout/TableUI";
import { Pagination, Switch } from "../globals";
import { returnEuroString, prettyDateFormat } from "../../helpers/Utils";
import AntdConfirm from "../../helpers/AntdConfirm";

const PER_PAGE = 50;

// Cases where the prime cost rose above every competitor price. The pricing rule cannot choose
// here, so an employee decides per product and department: keep the price that is live, or take it
// up to the cost based minimum.
const RetailCostReview = ({ page, setPage, onCountChange }) => {
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showDecided, setShowDecided] = useState(false);
  const [deciding, setDeciding] = useState(null);

  const fetchRows = useCallback(() => {
    setLoading(true);
    Axios.get("/api/v1/retail_cost_reviews", {
      params: {
        page,
        per_page: PER_PAGE,
        status: showDecided ? "all" : "pending",
      },
    })
      .then((res) => {
        setRows(res.data?.data || []);
        setTotals(res.data?.totals || 0);
      })
      .catch(() => message.error("Could not load cost review cases"))
      .finally(() => setLoading(false));
  }, [page, showDecided]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const decide = (row, decision) => {
    const label =
      decision === "keep"
        ? `Keep ${returnEuroString(row.current_price)} in ${row.department}?`
        : `Raise ${row.department} to ${returnEuroString(row.min_sale_price)}?`;

    AntdConfirm({
      title: label,
      content:
        decision === "keep"
          ? "The price stays as it is and the rule will not ask again until the cost changes."
          : "The new price is written and queued for the next export to Rivile.",
      onOk: async () => {
        setDeciding(row.id);
        try {
          const { data } = await Axios.post(
            `/api/v1/retail_cost_reviews/${row.id}/decide`,
            { decision }
          );
          if (data?.success) {
            message.success("Saved");
            fetchRows();
            if (onCountChange) onCountChange();
          } else {
            message.error(data?.message || "Could not save the decision");
          }
        } catch (e) {
          message.error(
            e?.response?.data?.message || "Could not save the decision"
          );
        } finally {
          setDeciding(null);
        }
      },
    });
  };

  return (
    <div className="row">
      <ContentBlock containerClass="pt-3">
        <div className="d-flex align-items-center justify-content-between px-2 pb-2">
          <div className="text-sm text-silver">
            Prime cost above every competitor price — {totals}{" "}
            {showDecided ? "case(s)" : "waiting for a decision"}
          </div>
          <div className="d-flex align-items-center">
            <span className="text-sm-label mr-2">Show decided</span>
            <Switch
              checked={showDecided}
              onChange={() => {
                setPage(1);
                setShowDecided((prev) => !prev);
              }}
            />
          </div>
        </div>
        <ContentValidation loader={loading} array={rows}>
          <TableUi
            hoverable={false}
            ths={[
              "Product",
              "Department",
              "Prime cost",
              "Cheapest competitor",
              "Current price",
              "Rule",
              "Decision",
            ]}
          >
            {rows.map((row) => (
              <TableUi.tr key={row.id}>
                <TableUi.td className={"p-left"}>
                  <div className="text-sm font-semi-bold">
                    {row.product_name || "-"}
                  </div>
                  <div className="text-xs text-silver">
                    {row.product_code || row.bb_sku}
                  </div>
                </TableUi.td>
                <TableUi.td>
                  <nobr>{row.department}</nobr>
                </TableUi.td>
                <TableUi.td>
                  <nobr className="text-danger">
                    {returnEuroString(row.cost_price)}
                  </nobr>
                  <div className="text-xs text-silver">
                    <nobr>min {returnEuroString(row.min_sale_price)}</nobr>
                  </div>
                </TableUi.td>
                <TableUi.td>
                  <nobr>
                    {row.competitor_price
                      ? returnEuroString(row.competitor_price)
                      : "-"}
                  </nobr>
                  <div className="text-xs text-silver">
                    {row.competitor_shop || "no competitors"}
                  </div>
                </TableUi.td>
                <TableUi.td>
                  <nobr>
                    {row.current_price
                      ? returnEuroString(row.current_price)
                      : "-"}
                  </nobr>
                </TableUi.td>
                <TableUi.td>
                  <div className="text-xs">{row.rule || "-"}</div>
                  <div className="text-xs text-silver">
                    {row.created_at && prettyDateFormat(row.created_at)}
                  </div>
                </TableUi.td>
                <TableUi.td>
                  {row.status === "pending" ? (
                    <div className="d-flex">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        disabled={deciding === row.id}
                        onClick={() => decide(row, "keep")}
                      >
                        <nobr>Keep price</nobr>
                      </button>
                      <Tooltip
                        placement="top"
                        title={`Write ${returnEuroString(
                          row.min_sale_price
                        )} as the new price`}
                      >
                        <button
                          type="button"
                          className="btn btn-sm btn-primary ml-2"
                          disabled={
                            deciding === row.id ||
                            !(Number(row.min_sale_price) > 0)
                          }
                          onClick={() => decide(row, "raise")}
                        >
                          <nobr>Raise to cost</nobr>
                        </button>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className="text-xs">
                      <div className="font-semi-bold">
                        {row.status === "kept"
                          ? "Price kept"
                          : row.status === "raised"
                            ? "Raised to cost"
                            : "Resolved by rule"}
                      </div>
                      <div className="text-silver">
                        {row.decided_by || "-"}
                        {row.decided_at
                          ? ` · ${prettyDateFormat(row.decided_at)}`
                          : ""}
                      </div>
                    </div>
                  )}
                </TableUi.td>
              </TableUi.tr>
            ))}
          </TableUi>
          <Pagination
            className="pb-3"
            setPagination={setPage}
            page={page}
            totals={totals}
            pageCount={PER_PAGE}
          />
        </ContentValidation>
      </ContentBlock>
    </div>
  );
};

export default RetailCostReview;
